/**
 * Privacy utility functions for checking column access permissions.
 *
 * These functions centralize the logic for determining when a column should be
 * blocked from access based on the disable, disable_preview, and privacy_level fields.
 */

export enum PrivacyLevel {
  BLOCKED = "blocked",
  REDACTED = "redacted",
  APPROVED = "approved",
  SYNC_ONLY = "syncOnly", // column can be synced to destination, but unusable inside Hightouch
  SYNC_BLOCKED = "syncBlocked", // full access to column within Hightouch, but can't be synced to destination
}

export interface ColumnPrivacyInfo {
  disable?: boolean;
  disablePreview?: boolean;
  privacyLevel: string | null;
}

// checks if columns is restricted based on whether query is accessing a column vs. syncing a column
// note: this exclusively checks privacyLevel and ignores disabled/enabled flag
export function isColumnRestricted(
  privacyLevel: PrivacyLevel,
  forSync: boolean,
): boolean {
  if (privacyLevel === PrivacyLevel.BLOCKED) {
    return true;
  }
  if (forSync && privacyLevel === PrivacyLevel.SYNC_BLOCKED) {
    return true;
  }
  if (!forSync && privacyLevel === PrivacyLevel.SYNC_ONLY) {
    return true;
  }

  return false;
}

/**
 * Determines if a column is completely blocked from access. (We should still allow viewing the listing in schema)
 * A column is blocked if:
 * - disable = true, OR
 * - privacyLevel = 'blocked'
 * - privacyLevel = 'syncOnly'
 *
 * When privacyLevel = 'blocked', it should behave identically to disable = true.
 * Only when privacyLevel is null or 'approved' should queries proceed.
 *
 * When privacyLevel = 'syncOnly', it behaves identically to blocked, except the column can be synced to destinations
 */
export function isColumnAccessBlocked(column: ColumnPrivacyInfo): boolean {
  if (column.disable) {
    return true;
  }
  const privacyLevel = column.privacyLevel as PrivacyLevel;
  switch (privacyLevel) {
    case PrivacyLevel.BLOCKED:
    case PrivacyLevel.SYNC_ONLY:
      return true;
    case PrivacyLevel.APPROVED:
    case PrivacyLevel.REDACTED:
    case PrivacyLevel.SYNC_BLOCKED:
      return false;
    case null:
      return false;
    // ensure we handle all PrivacyLevel enum values
    default:
      throw new Error(`Unknown privacy level: ${privacyLevel satisfies never}`);
  }
}

/**
 * Determines if a column can be synced to destination
 * A column is blocked from syncing if:
 * - disable = true, OR
 * - privacyLevel = 'blocked' OR
 * - privacyLevel = 'SyncBlocked'
 *
 */
export function isColumnSyncBlocked(column: ColumnPrivacyInfo): boolean {
  if (column.disable) {
    return true;
  }
  const privacyLevel = column.privacyLevel as PrivacyLevel;
  switch (privacyLevel) {
    case PrivacyLevel.BLOCKED:
    case PrivacyLevel.SYNC_BLOCKED:
      return true;
    case PrivacyLevel.APPROVED:
    case PrivacyLevel.REDACTED:
    case PrivacyLevel.SYNC_ONLY:
      return false;
    case null:
      return false;
    // ensure we handle all PrivacyLevel enum values
    default:
      throw new Error(`Unknown privacy level: ${privacyLevel satisfies never}`);
  }
}

/**
 * Determines if a column's preview/values should be blocked.
 * A column preview is blocked if:
 * - disable = true, OR
 * - disablePreview = true, OR
 * - privacyLevel = 'blocked', OR
 * - privacyLevel = 'redacted' OR
 * - privacyLevel = 'syncOnly'
 */
export function isColumnPreviewBlocked(column: ColumnPrivacyInfo): boolean {
  if (column.disable || column.disablePreview) {
    return true;
  }
  const privacyLevel = column.privacyLevel as PrivacyLevel;
  switch (privacyLevel) {
    case PrivacyLevel.BLOCKED:
    case PrivacyLevel.REDACTED:
    case PrivacyLevel.SYNC_ONLY:
      return true;
    case PrivacyLevel.APPROVED:
    case PrivacyLevel.SYNC_BLOCKED:
      return false;
    case null:
      return false;
    // ensure we handle all PrivacyLevel enum values
    default:
      throw new Error(`Unknown privacy level: ${privacyLevel satisfies never}`);
  }
}

/**
 * Determines if a column should be treated as having disabled preview.
 * This is used when we need to include the column in results but redact its values.
 */
export function isColumnPreviewDisabled(column: ColumnPrivacyInfo): boolean {
  return isColumnPreviewBlocked(column) && !isColumnAccessBlocked(column);
}

/**
 * Folds the legacy `disable` and `disable_preview` booleans into a single
 * PrivacyLevel value, so a single string can stand in for the column's
 * effective privacy state.
 *
 * Precedence:
 * - `disable=true` -> BLOCKED (operationally identical per `isColumnAccessBlocked`).
 * - When raw `privacyLevel` is BLOCKED / SYNC_ONLY / SYNC_BLOCKED, the raw
 *   value wins — these encode sync-perspective intent that `disable_preview`
 *   alone can't express. (A production audit at the time this helper was
 *   added found zero rows with `disable_preview=true AND privacy_level=
 *   'syncBlocked'`, so this branch is essentially defensive.)
 * - Otherwise, `disable_preview=true` promotes null/APPROVED/REDACTED to
 *   REDACTED.
 * - Falls through to the raw `privacyLevel` (possibly null) when no flag
 *   applies.
 *
 * Consumers asking "is preview blocked?" should still prefer
 * `isColumnPreviewBlocked` on the raw fields; comparing this value against
 * REDACTED-or-stronger misses the SYNC_BLOCKED + disable_preview case (which
 * folds to SYNC_BLOCKED here to preserve the sync-block signal).
 */
export function getEffectivePrivacyLevel(
  column: ColumnPrivacyInfo,
): PrivacyLevel | null {
  if (column.disable) return PrivacyLevel.BLOCKED;
  const raw = column.privacyLevel as PrivacyLevel | null;
  if (
    raw === PrivacyLevel.BLOCKED ||
    raw === PrivacyLevel.SYNC_ONLY ||
    raw === PrivacyLevel.SYNC_BLOCKED
  ) {
    return raw;
  }
  if (column.disablePreview) return PrivacyLevel.REDACTED;
  return raw;
}
